/**
 * 시스템 문서.
 *
 * 두 화면이다. **소개**(`/docs`)는 이 시스템이 무엇이고 왜 이렇게 생겼는지를 한 장으로
 * 말한다 — 처음 콘솔을 여는 사람, 도입을 검토하는 사람, 인수인계받는 사람이 여기서
 * 시작한다. **문서**(`/docs/:slug`)는 저장소의 설계 문서를 그대로 읽는다.
 *
 * 소개만 여기서 직접 쓴다. 나머지는 `docs/` 를 빌려 온다 — 같은 내용을 두 벌 쓰면
 * 반드시 갈리고, 갈린 문서는 틀렸다는 사실조차 드러나지 않는다.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Cpu,
  FileText,
  MonitorPlay,
  Server,
  ShieldCheck,
} from 'lucide-react';
import { Markdown, outlineOf } from '../components/markdown.js';
import { ErrorNote, Loading, Panel } from '../components/ui.js';
import { docBySlug, visibleDocs, type DocEntry } from '../docs/registry.js';
import { describeError } from '../hooks.js';

/* ────────────────────────────── 소개 ────────────────────────────── */

/**
 * 세 가지 설계 결정.
 *
 * 기획 문서 §0 의 요약이다. 여기 다시 적는 이유는, 이 셋을 모르면 나머지 문서가
 * 전부 "왜 이렇게 했지" 로 읽히기 때문이다. 문서를 여는 순서보다 이게 먼저다.
 */
const DECISIONS: ReadonlyArray<{ title: string; body: string; icon: typeof Server }> = [
  {
    title: '호스트 중립',
    body: '어느 서비스에 붙어도 SignageOne 은 자기 주체·역할·감사로그를 스스로 갖는다. 호스트는 표준 신원 토큰만 넘긴다. 그래서 한 곳에 종속되지 않는다.',
    icon: Server,
  },
  {
    title: '편성 해석은 순수 함수',
    body: '"지금 이 화면에 무엇을 트는가"를 DB·시간·네트워크에서 떼어낸 순수 함수로 만들었다. 이 로직이 틀리면 모든 화면이 동시에 틀린 것을 튼다. 제품의 심장이다.',
    icon: Cpu,
  },
  {
    title: '플레이어는 고집스럽게',
    body: '서버는 "지금 틀 것"만 내려주고 플레이어는 그것만 튼다. 대신 네트워크가 며칠 죽어도 마지막 편성을 계속 튼다. 검은 화면은 사고다.',
    icon: MonitorPlay,
  },
];

/** 규모 목표. 숫자가 있어야 "충분한가"를 논쟁이 아니라 측정으로 답할 수 있다. */
const TARGETS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '5,000대', label: '동시 운영 화면' },
  { value: '50,000건', label: '자산' },
  { value: '≤ 45초', label: '편성 변경 반영 p95' },
  { value: '7일', label: '오프라인 자율재생' },
];

/** 구성 요소. 셋으로 나뉘고, 그 경계가 곧 장애 격리 경계다. */
const PARTS: ReadonlyArray<{ name: string; role: string; detail: string }> = [
  {
    name: '콘솔',
    role: '사람이 정한다',
    detail: '무엇을 언제 어느 화면에 틀지 정하고, 지금 무엇이 나가는지 본다. 독립 웹으로도, 호스트 안 iframe 으로도 뜬다.',
  },
  {
    name: '서버',
    role: '해석하고 기록한다',
    detail: '편성을 해석해 "지금 틀 것"을 내려주고, 재생 증빙과 감사로그를 남긴다. 미디어는 단명 서명 주소로만 나간다.',
  },
  {
    name: '플레이어',
    role: '튼다, 멈추지 않는다',
    detail: '데스크톱·안드로이드·웹 어디서든 같은 코어가 돈다. 서버가 죽어도 캐시한 편성으로 계속 튼다.',
  },
];

export function DocsHomeScreen({ capabilities }: { capabilities: readonly string[] }): ReactNode {
  const docs = visibleDocs(capabilities);

  return (
    <div className="space-y-6">
      {/* 표지. 제품 이름과 한 줄 — 이 화면을 처음 여는 사람이 여기서 방향을 잡는다. */}
      <section
        className="relative overflow-hidden rounded-xl px-5 py-7 sm:px-8 sm:py-10"
        style={{ background: 'linear-gradient(135deg, rgb(var(--th-3)) 0%, rgb(var(--th-3) / 0.88) 100%)' }}
      >
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full"
          style={{ background: 'radial-gradient(circle, rgb(var(--th-5) / 0.18) 0%, transparent 70%)' }}
        />
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11.5px] font-medium tracking-wide text-th-6">
            <BookOpen className="h-3.5 w-3.5" strokeWidth={2} />
            시스템 문서
          </span>
          <h1 className="mt-4 text-[30px] font-semibold leading-tight tracking-tight text-white">
            SignageOne
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-300">
            호스트에 종속되지 않는 디지털 사이니지 플랫폼. 매장·로비·전시장의 화면 수천 대에
            <strong className="font-semibold text-white"> 무엇을 언제 틀지</strong> 정하고, 그것이
            실제로 나갔는지까지 증빙으로 남깁니다.
          </p>
          <p className="mt-4 text-[13.5px] leading-relaxed text-slate-400">
            이 문서 묶음은 그 시스템의 기획과 설계를 담고 있습니다. 왜 이렇게 만들었는지부터
            어떻게 붙이고 어떻게 지키는지까지, 저장소의 설계 문서를 그대로 읽습니다.
          </p>
        </div>
      </section>

      {/* 규모 목표 — "충분한가"를 논쟁이 아니라 숫자로 답한다. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {TARGETS.map((target) => (
          <div key={target.label} className="rounded-xl border border-slate-200 bg-white p-5">
            <p className="text-[22px] font-semibold tracking-tight text-slate-900">{target.value}</p>
            <p className="mt-1 text-[12.5px] text-slate-500">{target.label}</p>
          </div>
        ))}
      </div>

      <Panel
        title="핵심 설계 결정"
        help="다른 문서를 읽기 전에 알아 두어야 할 세 가지 설계 원칙입니다."
      >
        <div className="grid gap-4 p-5 lg:grid-cols-3">
          {DECISIONS.map((decision) => {
            const Icon = decision.icon;
            return (
              <div key={decision.title} className="rounded-lg border border-slate-200 bg-slate-50/60 p-5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-th-3">
                  <Icon className="h-[18px] w-[18px] text-th-6" strokeWidth={1.9} />
                </div>
                <h3 className="mt-3 text-[14px] font-semibold text-slate-900">{decision.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">{decision.body}</p>
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel title="구성" help="세 구성 요소의 경계가 곧 장애가 번지지 않는 경계입니다.">
        <ul className="divide-y divide-slate-100">
          {PARTS.map((part) => (
            <li key={part.name} className="flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-baseline sm:gap-5">
              <div className="w-24 shrink-0">
                <p className="text-[14px] font-semibold text-slate-900">{part.name}</p>
                <p className="text-[11.5px] text-slate-400">{part.role}</p>
              </div>
              <p className="text-[13px] leading-relaxed text-slate-600">{part.detail}</p>
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="문서" subtitle={`${docs.length}개 · 읽는 순서대로 정렬`}>
        <ul className="divide-y divide-slate-100">
          {docs.map((doc) => (
            <li key={doc.slug}>
              <Link
                to={`/docs/${doc.slug}`}
                className="group flex items-center gap-4 px-5 py-4 transition-colors hover:bg-slate-50"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white">
                  <FileText className="h-4 w-4 text-slate-400" strokeWidth={1.9} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-slate-900">
                    {doc.title}
                    {doc.capability === 'system' && (
                      <span className="ml-2 inline-flex items-center gap-1 align-middle text-[11px] font-medium text-slate-400">
                        <ShieldCheck className="h-3 w-3" strokeWidth={2} />
                        시스템
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-slate-500 sm:truncate">{doc.summary}</p>
                </div>
                <ArrowRight
                  className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500"
                  strokeWidth={2}
                />
              </Link>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

/* ────────────────────────────── 문서 ────────────────────────────── */

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; source: string }
  | { kind: 'error'; message: string };

export function DocScreen({ capabilities }: { capabilities: readonly string[] }): ReactNode {
  const { slug = '' } = useParams();
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  const doc = docBySlug(slug);
  const allowed = doc ? !doc.capability || capabilities.includes(doc.capability) : false;

  useEffect(() => {
    if (!doc || !allowed) return;

    // 화면을 떠난 뒤 도착한 응답이 다음 문서를 덮어쓰지 않게 한다.
    let live = true;
    setPhase({ kind: 'loading' });
    doc
      .load()
      .then((source) => {
        if (live) setPhase({ kind: 'ready', source });
      })
      .catch((error: unknown) => {
        if (live) setPhase({ kind: 'error', message: describeError(error) });
      });

    return () => {
      live = false;
    };
  }, [doc, allowed]);

  /*
   * 앵커로 들어왔으면 그 문단으로 옮겨 준다.
   *
   * 문서를 **다 그린 뒤에야** 그 자리가 생긴다. 그래서 내용이 준비된 다음에 찾는다 —
   * 주소만 보고 먼저 움직이면 언제나 맨 위에 멈춘다.
   */
  useEffect(() => {
    if (phase.kind !== 'ready') return;
    const anchor = location.hash.replace(/^#/, '');
    if (!anchor) {
      window.scrollTo({ top: 0 });
      return;
    }
    const target = document.getElementById(decodeURIComponent(anchor));
    target?.scrollIntoView({ block: 'start' });
  }, [phase.kind, location.hash, slug]);

  const outline = useMemo(
    () => (phase.kind === 'ready' ? outlineOf(phase.source) : []),
    [phase],
  );

  // 없는 문서·권한 없는 문서는 목록으로 돌린다. 빈 화면이나 403 을 보여줄 이유가 없다.
  if (!doc || !allowed) return <Navigate to="/docs" replace />;

  const docs = visibleDocs(capabilities);
  const at = docs.findIndex((entry) => entry.slug === doc.slug);
  const previous = at > 0 ? docs[at - 1] : undefined;
  const next = at >= 0 && at < docs.length - 1 ? docs[at + 1] : undefined;

  return (
    <div className="space-y-4">
      <Link
        to="/docs"
        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 transition-colors hover:text-slate-800"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
        시스템 문서
      </Link>

      <div className="flex gap-6">
        <article className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-5 sm:px-7 sm:py-6">
          {phase.kind === 'loading' && <Loading />}
          {phase.kind === 'error' && (
            <ErrorNote message={`문서를 불러오지 못했습니다 — ${phase.message}`} />
          )}
          {phase.kind === 'ready' && <Markdown source={phase.source} basePath={`/docs/${doc.slug}`} />}

          {phase.kind === 'ready' && (previous || next) && (
            <nav className="mt-10 flex items-stretch gap-3 border-t border-slate-200 pt-5">
              {previous ? (
                <Link
                  to={`/docs/${previous.slug}`}
                  className="group flex-1 rounded-lg border border-slate-200 px-4 py-3 transition-colors hover:border-slate-300 hover:bg-slate-50"
                >
                  <p className="text-[11.5px] text-slate-400">이전</p>
                  <p className="mt-0.5 text-[13.5px] font-medium text-slate-800">{previous.title}</p>
                </Link>
              ) : (
                <div className="flex-1" />
              )}
              {next ? (
                <Link
                  to={`/docs/${next.slug}`}
                  className="group flex-1 rounded-lg border border-slate-200 px-4 py-3 text-right transition-colors hover:border-slate-300 hover:bg-slate-50"
                >
                  <p className="text-[11.5px] text-slate-400">다음</p>
                  <p className="mt-0.5 text-[13.5px] font-medium text-slate-800">{next.title}</p>
                </Link>
              ) : (
                <div className="flex-1" />
              )}
            </nav>
          )}
        </article>

        {/*
          목차는 넓은 화면에서만 띄운다. 좁은 화면에서 본문 위에 얹으면 정작 읽을 것을
          가린다 — 그럴 바에는 없는 편이 낫다.
        */}
        {outline.length > 1 && (
          <aside className="hidden w-56 shrink-0 xl:block">
            <div className="sticky top-6">
              <p className="mb-2 px-3 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                목차
              </p>
              <ul className="max-h-[calc(100vh-8rem)] space-y-0.5 overflow-y-auto">
                {outline.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#/docs/${doc.slug}#${item.id}`}
                      className="block rounded-md px-3 py-1.5 text-[12.5px] leading-snug text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                    >
                      {item.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

export type { DocEntry };
